<template>
  <div class="gameboard-extended">
    <div class="gameboard__wrapper">
      <div class="gameboard__content">
        <!-- GAME DATA MANAGER -->
        <slot></slot>

        <!-- LEFT -->
        <div class="gameboard__left">
          <div class="gameboard__player">
            <GameTimeBar v-if="match.duration" :timeData="timeData[topColor]" />
            <div class="gameboard__player-info">
              <GamePlayerInfo
                :game-data="gameData"
                :color="topColor"
                status-position="top"
              />
              <GameClock
                v-if="match.duration"
                :key="`${topColor}_clock`"
                :timeData="timeData[topColor]"
              />
            </div>
          </div>

          <GameMessage :game-data="gameData" @onFlip="handleFlip" />

          <div class="gameboard__player gameboard__player-right">
            <div class="gameboard__player-info">
              <GameClock
                v-if="match.duration"
                :key="`${bottomColor}_clock`"
                :timeData="timeData[bottomColor]"
              />
              <GamePlayerInfo
                :game-data="gameData"
                :color="bottomColor"
                status-position="bottom"
              />
            </div>
            <GameTimeBar
              v-if="match.duration"
              :timeData="timeData[bottomColor]"
            />
          </div>
        </div>

        <!-- CENTER -->
        <div class="gameboard__center">
          <div class="gameboard__chessboard">
            <b-loading :is-full-page="false" :active="isFinalising">
              <p class="title is-3 is-spaced">Finalising match ...</p>
            </b-loading>
            <Chessboard
              ref="chessboard"
              :orientation="orientation"
              :pgn="pgn"
              :initialFen="initialFen"
              :movable="movable"
              :showThreats="showThreats"
              :premove="gameSettings.premove"
              :autoQueen="gameSettings.autoQueen"
              @onAfterMove="handleAfterMove"
            />
          </div>

          <!-- Request Draw and Resign buttons -->
          <div class="gameboard__user-actions">
            <RequestDraw
              v-if="playerColor && !match.result"
              :playerColor="playerColor"
              :match="match"
              :move="lastMove"
              @onRequestDraw="handleRequestDraw"
              @onAcceptDraw="handleAcceptDraw"
              @onDenyDraw="handleDenyDraw"
            />
            <Abort v-if="canAbort" @onAbort="handleAbort" />
            <Resign v-else-if="canResign" @onResign="handleResign" />
          </div>
        </div>

        <!-- RIGHT -->
        <div class="gameboard__right">
          <!-- Move history -->
          <MoveHistory
            :match="match"
            :moves-stats="movesStats"
            :black-to-start="blackToStart"
            :player-color="playerColor"
            @onViewMove="handleViewMove"
          />

          <!-- SETTINGS, REPORT, etc... -->
          <section
            v-if="currentProfile"
            class="gameboard__game-settings is-flex is-justify-content-flex-end"
          >
            <button
              class="button is-dark is-outlined"
              @click="handleAnalyseGame"
              v-if="currentProfile.isAdmin && match.result"
            >
              <b-icon icon="diagnoses"></b-icon>
              <span>Analyse Game</span>
            </button>

            <b-tooltip
              type="is-info"
              label="Download PGN"
              v-if="currentProfile.isAdmin && match.result"
            >
              <b-button
                icon-left="file-download"
                class="button is-info is-outlined"
                @click="handleDownloadPgn"
              ></b-button>
            </b-tooltip>

            <GameReport
              v-if="matchId"
              :profile="currentProfile"
              :url="'/#' + this.$route.path"
              :meta="{ firestoreTimeOffset, bindFirestore }"
            />

            <GameSettings :gameSettings="gameSettings" />
          </section>
        </div>
      </div>

      <!-- NAVIGATION -->
      <!-- Show to 2 players after match has ended -->
      <div v-if="tournamentId && playerColor && match && match.result">
        <button
          class="button is-primary is-outlined"
          @click="$router.push({ path: `/tournaments/${tournamentId}` })"
        >
          GO TO TOURNAMENT LOBBY
        </button>
      </div>

      <div
        class="gameboard__navigation"
        v-else-if="playerColor && match && match.result"
      >
        <InGameNav roomId="lobby" :match="match" />
      </div>

      <!-- ANALYSIS -->
      <div class="gameboard__analysis">
        <GameAnalysis :moves="movesStats" :match="match" ref="gameAnalysis" />
      </div>

      <!-- SPECTATORS -->
      <div v-if="match && match.startedAt" class="has-text-info mt-5">
        <span>
          {{ match.result ? "Replaying" : playerColor ? "" : "Spectating" }}
          Match
        </span>
        <span v-if="match.startedAt">
          {{ match.startedAt | asDateTime }}
        </span>
        <span v-if="match.endedAt">- {{ match.endedAt | asTime }}</span>
      </div>

      <!-- ROOM MATCH -->
      <div v-if="match && gameSettings">
        <RoomSpectators
          :roomId="matchId"
          :match="match"
          :showSpectators="gameSettings.showSpectators"
        />
      </div>
    </div>

    <div class="game-extended">
      <GameAdmin
        :match="match"
        :moves="movesStats"
        :moveIndex="selectedMoveIndex"
        :blackToStart="blackToStart"
      />

      <GameFocus
        v-if="playerColor && !match.result"
        :focusRef="matchRef.collection(`focuses-${playerColor}`)"
      />
    </div>

    <!-- PLAYERS/SIMULATORS -->
    <GamePlayerFactory
      v-if="isAivAiGame || playerColor"
      :ref="topColor"
      :color="topColor"
      :player="topPlayer"
      :lastMove="lastMove"
      :key="matchId + '-top'"
    />
    <GamePlayerFactory
      v-if="isAivAiGame || playerColor"
      :ref="bottomColor"
      :color="bottomColor"
      :player="bottomPlayer"
      :lastMove="lastMove"
      :key="matchId + '-bottom'"
    />
  </div>
</template>

<script>
import GameBoard from "@/components/games/GameBoard";
import Chessboard from "@/components/games/Chessboard";
import GamePlayerFactory from "@/components/games/GamePlayerFactory";
import MoveHistory from "@/components/games/MoveHistory";
import GamePlayerInfo from "@/components/games/GamePlayerInfo";
import GameClock from "@/components/games/GameClock";
import GameTimeBar from "@/components/games/GameTimeBar";
import GameMessage from "@/components/games/GameMessage";
import RequestDraw from "@/components/games/userActions/RequestDraw";
import Abort from "@/components/games/userActions/Abort";
import Resign from "@/components/games/userActions/Resign";
import GameSettings from "@/components/games/GameSettings";
import GameReport from "@/components/games/GameReport";
import GameAnalysis from "@/components/admins/GameAnalysis";
import InGameNav from "@/components/games/InGameNav";
import RoomSpectators from "@/components/rooms/RoomSpectators";
import GameAdmin from "@/components/games/GameAdmin";
import GameFocus from "@/components/games/GameFocus";

export default {
  name: "GameBoardExtended",

  extends: GameBoard,

  components: {
    GamePlayerFactory,
    Chessboard,
    MoveHistory,
    GamePlayerInfo,
    GameTimeBar,
    GameClock,
    GameMessage,
    RequestDraw,
    Abort,
    Resign,
    GameSettings,
    GameReport,
    GameAnalysis,
    InGameNav,
    RoomSpectators,
    GameAdmin,
    GameFocus,
  },

  props: {
    tournamentId: {
      type: String,
    },
  },
};
</script>

<style scoped>
.gameboard-extended {
  position: relative;
  height: 100%;
}
.gameboard__content {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  padding-bottom: 20px;
}
.gameboard__left {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0.5;
  width: 100%;
  height: 100%;
  padding-bottom: 20px;
}
.gameboard__center {
  margin: 0 20px;
}
.gameboard__right {
  flex: 0.5;
  min-width: 260px;
}
.gameboard__player {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.gameboard__player-info {
  display: flex;
  flex-direction: column;
  min-width: 260px;
}
.gameboard__user-actions {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.gameboard__game-settings {
  margin-top: 20px;
}
.gameboard__navigation {
  margin-bottom: 20px;
}
.gameboard__chessboard {
  width: 280px;
  height: 280px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
}

@media (min-width: 360px) {
  .gameboard__chessboard {
    width: 300px;
    height: 300px;
  }
}

@media (min-width: 420px) {
  .gameboard__chessboard {
    width: 320px;
    height: 320px;
  }
}

@media (min-width: 480px) {
  .gameboard__chessboard {
    width: 400px;
    height: 400px;
  }
}

@media (min-width: 576px) {
  .gameboard__chessboard {
    width: 480px;
    height: 480px;
  }

  .gameboard__left {
    flex-direction: row;
  }

  .gameboard__player.gameboard__player-right {
    flex-direction: column-reverse;
  }

  .gameboard__player-right >>> .gameboard__player-info {
    flex-direction: column-reverse;
  }
}

@media (min-width: 991px) {
  .gameboard__player-info {
    width: 100%;
    flex-direction: row;
  }
  .gameboard__player-right > .gameboard__player-info {
    flex-direction: row;
  }
}

@media (min-width: 1080px) {
  .gameboard__content {
    flex-direction: row;
  }

  .gameboard__left {
    flex-direction: column;
    height: 480px;
  }

  .gameboard__right {
    height: 480px;
  }

  .gameboard__player-info {
    flex-direction: column;
  }

  .gameboard__player.gameboard__player-right {
    flex-direction: column;
  }

  .gameboard__player-right > .gameboard__player-info {
    flex-direction: column;
  }
}

.title {
  z-index: 1;
  top: 240px;
  position: absolute;
}
</style>
